import { defineStore } from 'pinia'
import { useInventoryStore } from '~/stores/useInventoryStore'
import { GENERATIONS, getZone } from '~/data/zones'
import { getWildGoldPerKill } from '~/utils/wildGoldReward'
import {
  applyAscensionGoldCurve,
  candyPriceFactor,
  capGoldStack,
  DEFAULT_ECONOMY_MODIFIERS,
  evoItemPriceFactor,
  normalizeEconomyModifiers,
  scalePrice,
  setEconomyModifiers,
  type EconomyModifiers,
} from '~/utils/economyModifiers'
import { getStartersForGen } from '~/data/starters'
import {
  getNextBattleTowerZoneIndex,
  getBattleTowerZone,
  getBattleTowerProgressLabel,
  BATTLE_TOWER_FARM_ZONE_INDEX,
  battleTowerFarmUnlocked,
  isBattleTowerUnlocked,
  isPaldeaEndgameFrontierSuperseded,
  getFrontierDisplayStage,
  PALDEA_CHAMPION_BOSS_KEYS,
} from '~/data/battleTower'
import { getPokemonTypes, type PokemonType } from '~/data/types'
import { parseDescenteRun, type DescenteSanctuaireRunState } from '~/data/descenteSanctuaire'
import { useLocale } from '~/composables/useLocale'
import { getGenerationLabel } from '~/utils/generationLabels'
import {
  ZONE_MASTERY_THRESHOLDS,
  ZONE_MASTERY_BONUSES,
  getZoneMasteryLevel,
  getZoneMasteryBonusesForLevel,
  getZoneMasteryCandyQtyPurchased,
  resolveZoneMasteryCombatContext,
  resolveZoneMasteryCandyPurchaseContext,
  getAccountZoneMasteryLevel,
  getAccountZoneMasteryProgress,
  sumZoneMasteryKillsFromStats,
  listZoneMasteryRouteKills,
} from '../../../shared/zone_mastery'
import {
  clampOfflineCapHours,
  mergeOfflineShopProgressIntoStats,
  OFFLINE_CAP_BASE_HOURS,
  OFFLINE_EFFICIENCY_BASE_ABSENT_PERCENT,
  OFFLINE_EFFICIENCY_MAX_BONUS_PERCENT,
  type OfflineShopProgress,
} from '~/utils/offlineShopProgress'
import {
  ASCENSION_POWER_LEVEL_CAP,
  ASCENSION_POWER_STEP,
  getAscensionCombatPressureMult,
  getBattleTowerBossHpMult,
  getRouteEnemyHpMult,
} from '../../../shared/ascension_balance'
import type { MasteryAutoFarmMode } from '~/utils/masteryAutoFarmCore'
import { FRONTIER_KILLS_PER_STAGE } from '../../../shared/combat_balance'
import { getAscensionUnlockKills } from '../../../shared/ascension_unlock'
import { getGoldWellDpsMult } from '../../../shared/gold_well'
import { getAscensionTranscendenceDpsMult } from '../../../shared/ascension_transcendence'
import {
  getAscensionKeystoneSlots,
  getCombinedKeystoneEffect,
  type AscensionKeystoneEffect,
} from '../../../shared/ascension_keystones'
import { getAscensionShinyMult } from '../../../shared/ascension_shiny'
import { getZoneMasteryKillsPerStageMult } from '../../../shared/zone_mastery_rerun'
import {
  getAscensionRunMutatorEffect,
  normalizeAscensionRunMutatorId,
  type AscensionRunMutatorId,
} from '../../../shared/ascension_mutators'
import {
  ASCENSION_POST_BOOST_MULT,
  ASCENSION_ROUTE_SURGE_KILLS_MULT,
  isAscensionRouteSurgeActive,
} from '../../../shared/ascension_awakening'
import {
  DEFAULT_OFFLINE_SHINY_REVEAL,
  normalizeOfflineShinyRevealPreference,
  type OfflineShinyRevealPreference,
} from '~/data/offlineReportPrefs'

const BONUSES_KEY = 'poke-idle-bonuses'

function loadBonusesFromStorage(): { teamDpsBonus: number } {
  try {
    const raw = localStorage.getItem(BONUSES_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return { teamDpsBonus: 0 }
}

function saveBonusesToStorage(teamDpsBonus: number) {
  try {
    localStorage.setItem(BONUSES_KEY, JSON.stringify({ teamDpsBonus }))
  } catch { /* ignore */ }
}

function isFarmSessionSuppressed(state: { farmSessionSuppressedUntil?: number }): boolean {
  return Date.now() < (state.farmSessionSuppressedUntil ?? 0)
}

function resolveCandyPurchaseMasteryForState(
  state: Pick<
    PlayerState,
    | 'combatGeneration'
    | 'combatZone'
    | 'currentGeneration'
    | 'currentZone'
    | 'stats'
    | 'lastSelectedFarmRoute'
    | 'farmSessionSuppressedUntil'
  >,
) {
  return resolveZoneMasteryCandyPurchaseContext({
    combatGeneration: isFarmSessionSuppressed(state) ? null : state.combatGeneration,
    combatZone: isFarmSessionSuppressed(state) ? null : state.combatZone,
    currentGeneration: state.currentGeneration,
    currentZone: state.currentZone,
    stats: state.stats as Record<string, unknown>,
    lastFarmRoute: state.lastSelectedFarmRoute,
  })
}

/** Farm actif = coords combat dédiées, sauf après un « Retour frontière » explicite. */
function isFarmCombatActive(
  state: Pick<PlayerState, 'combatGeneration' | 'farmSessionSuppressedUntil'>,
): boolean {
  if (isFarmSessionSuppressed(state)) return false
  return state.combatGeneration !== null
}

// ── Lure System ──────────────────────────────────────────────────────────────
export type LureType = 'shiny' | 'gold'

export interface LureDefinition {
  type: LureType
  labelFr: string
  labelEn: string
  descFr: string
  descEn: string
  icon: string
  costTokens: number
  killDuration: number
  goldMult: number
  shinyMult: number
}

export const LURE_DEFINITIONS: LureDefinition[] = [
  {
    type: 'gold',
    labelFr: 'Leurre Doré',
    labelEn: 'Gold Lure',
    descFr:
      '+40 % d’or sur les récompenses de fin de combat (sauvages, boss, tour). 150 kills. N’affecte pas la gacha, la boutique, etc.',
    descEn:
      '+40% gold on end-of-combat rewards (wilds, bosses, tower). 150 kills. Does not affect gacha, shop, etc.',
    icon: '💰',
    costTokens: 75,
    killDuration: 150,
    goldMult: 1.40,
    shinyMult: 1,
  },
  {
    type: 'shiny',
    labelFr: 'Leurre Chromatique',
    labelEn: 'Shiny Lure',
    descFr:
      '+50 % de chance qu’un Pokémon sauvage soit chromatique (routes + ferme Tour). Un shiny sauvage = beaucoup plus d’or et d’XP sur ce kill. 100 kills. Sans effet sur la gacha, les œufs ou le hors-combat.',
    descEn:
      '+50% chance a wild Pokémon is shiny (routes + Tower farm). A shiny wild = much more gold and XP on that kill. 100 kills. No effect on gacha, eggs, or out-of-combat rewards.',
    icon: '✨',
    costTokens: 200,
    killDuration: 100,
    goldMult: 1,
    shinyMult: 1.50,
  },
]

// ── Zone Mastery (shared avec le serveur) ─────────────────────────────────────
export {
  ZONE_MASTERY_THRESHOLDS,
  ZONE_MASTERY_BONUSES,
  getZoneMasteryLevel,
  getZoneMasteryBonusesForLevel,
  getZoneMasteryCandyQtyPurchased,
  resolveZoneMasteryCombatContext,
  resolveZoneMasteryCandyPurchaseContext,
  getAccountZoneMasteryLevel,
  getAccountZoneMasteryProgress,
  sumZoneMasteryKillsFromStats,
  listZoneMasteryRouteKills,
}

/** Appel paresseux : évite useLocale() au chargement du module (cycle usePlayerStore ↔ useLocale). */
function localeApi() {
  return useLocale()
}

const STAGES_PER_ZONE = 10
/** Suppression farm « permanente » jusqu'à un choix explicite de route (`travelTo`). */
export const FARM_SUPPRESSION_HOLD_UNTIL = 9_007_199_254_740_991

function formatFrontierProgressLabel(
  state: Pick<PlayerState, 'currentGeneration' | 'currentZone' | 'currentStage' | 'defeatedBosses'>,
): string {
  const region = getGenerationLabel(state.currentGeneration, localeApi().t, '???')
  const stage = getFrontierDisplayStage(
    state.currentGeneration,
    state.currentZone,
    state.currentStage,
    state.defeatedBosses,
  )
  return `${region} · Zone ${state.currentZone} · ${stage}/${STAGES_PER_ZONE}`
}

function formatFarmRouteLabel(
  state: Pick<
    PlayerState,
    | 'combatGeneration'
    | 'combatZone'
    | 'battleTowerZone'
    | 'currentGeneration'
    | 'currentZone'
    | 'currentStage'
    | 'defeatedBosses'
  >,
): string {
  if (state.battleTowerZone === BATTLE_TOWER_FARM_ZONE_INDEX) {
    return localeApi().t('Réserve — farm sanctuaire', 'Reserve — sanctuary farm')
  }
  const gen = state.combatGeneration
  const zoneId = state.combatZone
  if (gen == null || zoneId == null) {
    return formatFrontierProgressLabel(state)
  }
  const zoneDef = getZone(gen, zoneId)
  if (zoneDef) return localeApi().t(zoneDef.nameFr, zoneDef.nameEn)
  const region = getGenerationLabel(gen, localeApi().t, '???')
  return `${region} · Zone ${zoneId}`
}

function resolveTowerZoneForDisplay(
  state: Pick<
    PlayerState,
    | 'battleTowerZone'
    | 'defeatedBosses'
    | 'battleTowerDefeatedTypes'
    | 'battleTowerCapstoneDefeated'
    | 'battleTowerLategameDisabled'
    | 'role'
    | 'combatGeneration'
    | 'farmSessionSuppressedUntil'
  >,
): number | null {
  if (state.battleTowerZone != null) return state.battleTowerZone
  if (isFarmCombatActive(state)) return null
  if (!isBattleTowerUnlocked(state.defeatedBosses)) return null
  if (state.battleTowerLategameDisabled && state.role !== 'admin') return null
  return getNextBattleTowerZoneIndex(
    state.battleTowerDefeatedTypes,
    state.battleTowerCapstoneDefeated,
  )
}

function buildActiveProgressLabel(
  state: Pick<
    PlayerState,
    | 'currentGeneration'
    | 'currentZone'
    | 'currentStage'
    | 'defeatedBosses'
    | 'battleTowerZone'
    | 'battleTowerDefeatedTypes'
    | 'battleTowerCapstoneDefeated'
    | 'battleTowerLategameDisabled'
    | 'role'
    | 'combatGeneration'
    | 'combatZone'
    | 'farmSessionSuppressedUntil'
  >,
  options?: { farmPrefix?: boolean },
): string {
  const loc = localeApi().locale.value
  if (state.battleTowerZone === BATTLE_TOWER_FARM_ZONE_INDEX) {
    const route = formatFarmRouteLabel(state)
    return options?.farmPrefix ? `${localeApi().t('Farm', 'Farm')} — ${route}` : route
  }
  const towerZn = resolveTowerZoneForDisplay(state)
  if (towerZn != null) {
    const label = getBattleTowerProgressLabel(towerZn, loc)
    if (label) return label
  }
  if (isFarmCombatActive(state)) {
    const route = formatFarmRouteLabel(state)
    return options?.farmPrefix ? `${localeApi().t('Farm', 'Farm')} — ${route}` : route
  }
  return formatFrontierProgressLabel(state)
}
const KILLS_PER_STAGE = FRONTIER_KILLS_PER_STAGE
const ASCENSION_GOLD_STEP = 0.1
const ASCENSION_REWARD_GOLD_STEP = 0.05
const ASCENSION_XP_STEP = 0.05
const ASCENSION_DPS_STEP = 0.05

/** Niveau dresseur max (anti-cheat aligné serveur). */
export const MAX_PLAYER_LEVEL = 50000

/** Gens 1–9 toutes complétées → bonus Maître Pokédex (×3 shiny) — dérivé de `completedPokedexGens` au load */
const POKEDEX_MASTER_GEN_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const

function xpForLevel(level: number): number {
  if (level <= 1) return 0
  // Augmentation: 75 → 120 (×1.6) + exposant 1.9 → 2.0
  return Math.floor(120 * Math.pow(level, 2.0))
}

function roundAscensionMultiplier(value: number): number {
  const safe = Number.isFinite(value) ? value : 1
  return Math.round(safe * 100) / 100
}

export type CandySize = 'S' | 'M' | 'L' | 'XL'

const CASINO_PAYTABLE_SYMBOL_IDS = new Set(['cherry', 'orange', 'bar', 'seven', 'pikachu'])

export interface CasinoPaytableTierReward {
  tokens: number
  candies: Partial<Record<CandySize, number>>
}

export interface CasinoPaytableRow {
  symbols: [string, string, string]
  rewardsByTier: CasinoPaytableTierReward[]
}

function normalizeCasinoPaytable(raw: unknown): CasinoPaytableRow[] {
  if (!Array.isArray(raw)) return []
  const rows: CasinoPaytableRow[] = []
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue
    const sym = (row as { symbols?: unknown }).symbols
    if (!Array.isArray(sym) || sym.length !== 3) continue
    const triple = [String(sym[0] ?? ''), String(sym[1] ?? ''), String(sym[2] ?? '')] as [
      string,
      string,
      string,
    ]
    if (!triple.every((x) => CASINO_PAYTABLE_SYMBOL_IDS.has(x))) continue
    const rt = (row as { rewardsByTier?: unknown }).rewardsByTier
    if (!Array.isArray(rt) || rt.length < 5) continue
    const rewardsByTier: CasinoPaytableTierReward[] = []
    for (let i = 0; i < 5; i++) {
      const c = rt[i] as { tokens?: unknown; candies?: unknown } | undefined
      const cd =
        c?.candies && typeof c.candies === 'object' ? (c.candies as Record<string, unknown>) : {}
      const candies: Partial<Record<CandySize, number>> = {}
      for (const k of ['S', 'M', 'L', 'XL'] as CandySize[]) {
        const v = cd[k]
        if (v === undefined || v === null) continue
        const n = Math.max(0, Math.floor(Number(v)))
        if (n > 0) candies[k] = n
      }
      rewardsByTier.push({
        tokens: Math.max(0, Math.floor(Number(c?.tokens ?? 0))),
        candies,
      })
    }
    rows.push({ symbols: triple, rewardsByTier })
  }
  return rows
}

export const CANDY_XP: Record<CandySize, number> = { S: 1275, M: 2550, L: 5100, XL: 8500 }

// Ratios = XP — même XP/or pour toutes les tailles : S≈2 kills, M≈4.5, L≈9, XL=15
const CANDY_COST_BASE: Record<CandySize, number> = { S: 1500, M: 3000, L: 6000, XL: 10000 }

/** Milieu de la zone 4 : référence économie sauvage. */
const CANDY_PRICE_ANCHOR_ZONE = 4
const CANDY_PRICE_ANCHOR_STAGE = 5
/** Combats sauvages équivalents pour le prix d’un bonbon XL (augmenter = bonbons plus chers). */
const COMBATS_PER_XL_CANDY = 15

/**
 * Prix bonbon — déjà indexé sur l'or/kill (donc sensible au robinet), plus le levier
 * prix dédié de l'admin. Miroir exact de `getCandyCostForGeneration` (serveur).
 */
export function getCandyCost(
  size: CandySize,
  generation: number,
  mods?: EconomyModifiers,
): number {
  const g = Math.max(1, Math.min(9, generation))
  const goldPerKill = getWildGoldPerKill(g, CANDY_PRICE_ANCHOR_ZONE, CANDY_PRICE_ANCHOR_STAGE)
  const xlPrice = COMBATS_PER_XL_CANDY * goldPerKill
  return Math.max(
    1,
    scalePrice((xlPrice * CANDY_COST_BASE[size]) / CANDY_COST_BASE.XL, candyPriceFactor(mods)),
  )
}

/** Prix d'un objet d'évolution — miroir de `getEvolutionItemCostForGeneration` (serveur). */
const EVO_ITEM_BASE_PRICES: Record<number, number> = {
  1: 1000,
  2: 2000,
  3: 5000,
  4: 10000,
  5: 20000,
  6: 40000,
  7: 80000,
  8: 140000,
  9: 200000,
}

export function getEvolutionItemCost(generation: number, mods?: EconomyModifiers): number {
  const g = Math.max(1, Math.min(9, Math.floor(Number(generation) || 1)))
  return Math.max(1, scalePrice(EVO_ITEM_BASE_PRICES[g] ?? 200000, evoItemPriceFactor(mods)))
}

// Legacy (affichages figés gen 1)
export const CANDY_COST: Record<CandySize, number> = {
  S: getCandyCost('S', 1),
  M: getCandyCost('M', 1),
  L: getCandyCost('L', 1),
  XL: getCandyCost('XL', 1),
}

export interface DailyQuestState {
  id: string
  metric: 'totalKills' | 'totalDamage' | 'totalSummons'
  target: number
  progress: number
  rewardTokens: number
  rewardKind: 'tokens' | 'candy_xl'
  rewardAmount: number
  rewardCandySize: CandySize | null
  completed: boolean
  claimed: boolean
  titleFr: string
  titleEn: string
  descriptionFr: string
  descriptionEn: string
}

export interface RunContractState {
  id: string
  metric: 'totalKills' | 'totalDamage' | 'totalSummons'
  target: number
  progress: number
  rewardTokens: number
  rewardKind: 'tokens' | 'candy_xl'
  rewardAmount: number
  rewardCandySize: CandySize | null
  completed: boolean
  claimed: boolean
  expiresAt: number
  titleFr: string
  titleEn: string
  descriptionFr: string
  descriptionEn: string
}

export interface WeeklyQuestState {
  id: string
  metric: string
  target: number
  progress: number
  rewardTokens: number
  rewardKind: 'tokens' | 'candy_xl'
  rewardAmount: number
  rewardCandySize: CandySize | null
  completed: boolean
  claimed: boolean
  resetsAtMs: number
  titleFr: string
  titleEn: string
  descriptionFr: string
  descriptionEn: string
}

type QuestProgressMetric = 'totalKills' | 'totalDamage' | 'totalSummons'
type ProgressQuestState = {
  metric: string
  target: number
  progress: number
  completed: boolean
  claimed: boolean
}

export interface RegularityMilestone {
  count: number
  labelFr: string
  labelEn: string
  rewardTokens: number
  rewardCandyXL: number
  claimed: boolean
  unlocked: boolean
}

export interface RegularityState {
  count: number
  milestones: RegularityMilestone[]
  nextMilestone: number | null
}

export interface MonthlyCalendarState {
  month: string
  daysLogged: number[]
  totalDays: number
}

export interface AudioPreferences {
  enabled: boolean
  musicEnabled: boolean
  sfxEnabled: boolean
  /** Sons d’interface (menus, toggles). */
  sfxUiEnabled: boolean
  /** Lancer Pokéball, verrouillage, invocation. */
  sfxInvocationEnabled: boolean
  /** Rencontres (ex. descente sanctuaire). */
  sfxEncounterEnabled: boolean
  /** Jingle / effet rencontre chromatique. */
  sfxShinyEncounterEnabled: boolean
  /** Début de boss, échec boss. */
  sfxBossEventsEnabled: boolean
  /** KO ennemi (hors sauvage si KO sauvage OFF), victoire boss. */
  sfxCombatEnabled: boolean
  /** Montée de niveau, obtention de badge. */
  sfxProgressEnabled: boolean
  criesEnabled: boolean
  wildKillSfxEnabled: boolean
  musicMode: 'generated' | 'streaming'
  externalTrackUrl: string
  resumeStreamingFromLastPosition: boolean
  shinyEasterEggEnabled: boolean
  audioPack: 'classic8' | 'gba' | 'ds' | 'modernRetro' | 'ostInspired'
  autoPackByGeneration: boolean
  streamingCrossfadeMs: number
  masterVolume: number
  musicVolume: number
  sfxVolume: number
}

const DEFAULT_AUDIO_PREFERENCES: AudioPreferences = {
  enabled: false,
  musicEnabled: true,
  sfxEnabled: true,
  sfxUiEnabled: true,
  sfxInvocationEnabled: true,
  sfxEncounterEnabled: true,
  sfxShinyEncounterEnabled: true,
  sfxBossEventsEnabled: true,
  sfxCombatEnabled: true,
  sfxProgressEnabled: true,
  criesEnabled: true,
  wildKillSfxEnabled: false,
  musicMode: 'generated',
  externalTrackUrl: '',
  resumeStreamingFromLastPosition: true,
  shinyEasterEggEnabled: false,
  audioPack: 'classic8',
  autoPackByGeneration: false,
  streamingCrossfadeMs: 500,
  masterVolume: 70,
  musicVolume: 45,
  sfxVolume: 70,
}

function clampAudioVolume(value: unknown, fallback: number): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(0, Math.min(100, Math.round(parsed)))
}

function clampCrossfadeMs(value: unknown, fallback: number): number {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(200, Math.min(2000, Math.round(parsed)))
}

function normalizeAudioPreferences(raw: unknown): AudioPreferences {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_AUDIO_PREFERENCES }
  const prefs = raw as Partial<AudioPreferences>
  return {
    enabled: Boolean(prefs.enabled),
    musicEnabled: prefs.musicEnabled !== false,
    sfxEnabled: prefs.sfxEnabled !== false,
    sfxUiEnabled: prefs.sfxUiEnabled !== false,
    sfxInvocationEnabled: prefs.sfxInvocationEnabled !== false,
    sfxEncounterEnabled: prefs.sfxEncounterEnabled !== false,
    sfxShinyEncounterEnabled: prefs.sfxShinyEncounterEnabled !== false,
    sfxBossEventsEnabled: prefs.sfxBossEventsEnabled !== false,
    sfxCombatEnabled: prefs.sfxCombatEnabled !== false,
    sfxProgressEnabled: prefs.sfxProgressEnabled !== false,
    criesEnabled: prefs.criesEnabled !== false,
    wildKillSfxEnabled: Boolean(prefs.wildKillSfxEnabled),
    musicMode:
      prefs.musicMode === 'streaming'
        ? prefs.musicMode
        : 'generated',
    externalTrackUrl:
      typeof prefs.externalTrackUrl === 'string'
        ? prefs.externalTrackUrl.trim()
        : '',
    resumeStreamingFromLastPosition: prefs.resumeStreamingFromLastPosition !== false,
    shinyEasterEggEnabled: Boolean(prefs.shinyEasterEggEnabled),
    streamingCrossfadeMs: clampCrossfadeMs(prefs.streamingCrossfadeMs, DEFAULT_AUDIO_PREFERENCES.streamingCrossfadeMs),
    audioPack:
      prefs.audioPack === 'gba' || prefs.audioPack === 'ds' || prefs.audioPack === 'modernRetro' || prefs.audioPack === 'ostInspired'
        ? prefs.audioPack
        : 'classic8',
    autoPackByGeneration: Boolean(prefs.autoPackByGeneration),
    masterVolume: clampAudioVolume(prefs.masterVolume, DEFAULT_AUDIO_PREFERENCES.masterVolume),
    musicVolume: clampAudioVolume(prefs.musicVolume, DEFAULT_AUDIO_PREFERENCES.musicVolume),
    sfxVolume: clampAudioVolume(prefs.sfxVolume, DEFAULT_AUDIO_PREFERENCES.sfxVolume),
  }
}

/** État casino (quotas + paliers) — aligné serveur `casino_slot_service`. */
export interface MinigamesSummary {
  voltorbStartsToday: number
  voltorbMaxStartsPerDay: number
  voltorbLifetimeWins: number
  quizAnswered: number
  quizQuestionsPerDay: number
  quizCompleted: boolean
  nextResetAtMs: number
}

export interface CasinoState {
  spinsToday: number
  goldWageredToday: number
  maxSpinsPerDay: number
  maxGoldWageredPerDay: number
  nextResetAtMs: number
  betTiers: number[]
  tierMinLevel: number[]
  maxUnlockedTierIndex: number
  /** Tableau des gains (serveur `buildCasinoPaytable`). */
  paytable: CasinoPaytableRow[]
}

export interface InvocationFirstShinyPityStatus {
  eligible: boolean
  withoutShiny: number
  remaining: number
  hardMax: number
}

function defaultCasinoState(): CasinoState {
  return {
    spinsToday: 0,
    goldWageredToday: 0,
    maxSpinsPerDay: 35,
    maxGoldWageredPerDay: 125_000_000,
    nextResetAtMs: 0,
    betTiers: [5_000, 25_000, 125_000, 625_000, 3_125_000],
    tierMinLevel: [1, 25, 60, 120, 250],
    maxUnlockedTierIndex: 0,
    paytable: [],
  }
}

interface PlayerState {
  username: string
  /** Solde or en banque (farm crédité via POST /farm-sync). */
  serverConfirmedGold: number
  /** Or farm non encore flushé — aligné sur `combatGoldAccrual` via syncAccrualRef. */
  goldPendingLocal: number
  xp: number
  level: number
  currentGeneration: number
  currentZone: number
  currentStage: number
  stageKills: number
  teamDpsBonus: number
  badges: number
  defeatedBosses: string[] // Boss slugs defeated (unique)
  isLoggedIn: boolean
  candies: Record<CandySize, number>
  regionUnlockMessage: string | null
  combatGeneration: number | null
  combatZone: number | null
  adminVersion: number
  shinyCharms: number
  completedPokedexGens: number[]
  pokedexMaster: boolean
  totalKills: number
  goldWellLevel: number
  goldWellEnabled: boolean
  prestigeVaultEnabled: boolean
  ascensionShinyBonusEnabled: boolean
  zoneMasteryRerunAccelEnabled: boolean
  ascensionAwakeningPackEnabled: boolean
  ascensionRunMutatorsEnabled: boolean
  ascensionTranscendenceLevel: number
  ascensionKeystones: string[]
  ascensionSynergyEnabled: boolean
  /**
   * Modificateurs d'économie pilotés par l'admin (neutres par défaut).
   * Copie réactive du registre ambiant : garantit que les prix affichés se
   * recalculent quand un push `economy:update` arrive.
   */
  economyModifiers: EconomyModifiers
  penaltyType: 'dps' | 'gold' | null
  penaltyPercent: number
  avatarUrl: string | null
  stats: Record<string, number>
  showcase: Array<{ slug: string; isShiny: boolean }>
  achievements: string[]
  selectedTitle: string | null
  selectedAvatarFrame: string | null
  selectedProfileBanner: string | null
  selectedHudTheme: string | null
  /** Masquer l'équipe sur le profil public (/players, amis). */
  hideTeamFromProfile: boolean
  /** Paliers mécénat : 0–3 */
  supporterTier: number
  supporterSince: string | null
  /** Masque le badge mécène (cosmétiques toujours équipables). */
  hideSupporterBadge: boolean
  /** Bêta « nouvelle interface » (refonte HUD), enregistrée côté serveur. */
  newInterfaceEnabled: boolean
  /** Cosmétiques communautaires débloqués (jauge infra Ko-fi). */
  communityUnlockedCosmeticIds: string[]
  /** Progression jauge infra (% de l'objectif courant, ex. 15 à 225 € / 1500 €). */
  serverGoalPercentage: number
  chosenStarters: Record<number, string>
  goldNonce: number
  /** Mode endgame secret : zones 1–18, Red 19, farm 20 ; null = hors mode */
  battleTowerZone: number | null
  /** Progression secrète (persisté) */
  battleTowerDefeatedTypes: PokemonType[]
  /** Red (zone 19) vaincu — stats._btCapstoneDefeated */
  battleTowerCapstoneDefeated: boolean
  /** Rôle serveur (champion → tour si admin malgré flag global) */
  role: 'user' | 'admin' | 'helper'
  tokens: number
  extraDaycareSlots: number
  offlineCapHours: number
  /** % or offline onglet fermé (28 → 40 avec upgrades efficacité). */
  offlineGoldEfficiencyPercent: number
  /** Toutes les dépenses jetons boutique complétées (quêtes → bonbons XL). */
  tokenShopFullyUnlocked: boolean
  dailyQuests: DailyQuestState[]
  runContracts: RunContractState[]
  weeklyQuests: WeeklyQuestState[]
  monthlyQuests: WeeklyQuestState[]
  regularityState: RegularityState
  monthlyCalendar: MonthlyCalendarState
  /** Mini-jeux : machine à sous (quotas journaliers + paliers de mise). */
  casino: CasinoState
  /** Résumé mini-jeux (quotas) — sans états secrets. */
  minigames: MinigamesSummary | null
  /** Flag global : tour Paldea endgame désactivée pour les joueurs */
  battleTowerLategameDisabled: boolean
  /** Pity invocations par bannière (serveur). */
  invocationPity: Record<
    string,
    { withoutEpicPlus: number; withoutLegendary: number; withoutNew: number }
  >
  /** Garantie unique du premier shiny obtenu en invocation. */
  invocationFirstShinyPity: InvocationFirstShinyPityStatus
  /** Victoire de zone en attente: le joueur choisit rester/continuer. */
  zoneVictoryPrompt: { generation: number; zone: number } | null
  /** Progression suivante disponible après avoir choisi de rester. */
  zoneAdvanceReady: { generation: number; zone: number } | null
  /** Comportements de confort pour fluidifier frontière ↔ farm ↔ boss. */
  combatAutomation: {
    bossVictoryMode: 'manual' | 'stay' | 'continue'
    autoFightBossIfBattable: boolean
    autoRetreatToLastFarmRouteOnBossFailure: boolean
    /** Auto farm maîtrise : `off`, route cible, ou rotation prochain palier. */
    masteryAutoFarmMode: MasteryAutoFarmMode
    masteryAutoFarmTarget: { generation: number; zone: number } | null
    /** Palier route (1–5) : rotation / arrêt en mode cible. */
    masteryAutoFarmStopAtZoneTier: number
  }
  /** Dernière route de farm rejointe pour y revenir automatiquement en cas d’échec boss. */
  lastSelectedFarmRoute: { generation: number; zone: number } | null
  /** Bloque le repli auto farm juste après un clic « Retour frontière » (timestamp ms). */
  frontierReturnGuardUntil: number
  /** Ignore combatGeneration côté UI/combat après sortie explicite du farm (timestamp ms). */
  farmSessionSuppressedUntil: number
  /** Route de farm favorite pour y revenir en un clic. */
  favoriteFarmRoute: { generation: number; zone: number } | null
  /** Préférences audio globales (OFF par défaut). */
  audioPreferences: AudioPreferences
  /** Écriture inclusive (joueur·euse, dresseur·euse, reformulations épicènes). */
  inclusiveWriting: boolean
  /** Monnaie roguelite « Descente du sanctuaire ». */
  descenteSanctuaireShards: number
  /** Run active (null si aucune). */
  descenteSanctuaireRun: DescenteSanctuaireRunState | null
  ascensionLevel: number
  ascensionPoints: number
  ascensionSpent: number
  ascensionUpgrades: {
    xpBoost: number
    goldBoost: number
    enemyMitigation: number
    starterMomentum: number
  }
  /** Leurre actif (null = aucun). Décrémenté à chaque kill dans useCombatLoop. */
  activeLure: { type: LureType; killsRemaining: number } | null
  /** Animation shiny au rapport hors ligne : always | never | ask. */
  offlineShinyReveal: OfflineShinyRevealPreference
}

type ZoneAdvanceTarget = { generation: number; zone: number }

const CANDY_SIZES: CandySize[] = ['S', 'M', 'L', 'XL']

export function getCandyTotalPieces(candies: Partial<Record<CandySize, number>> | undefined): number {
  return CANDY_SIZES.reduce(
    (sum, size) => sum + Math.max(0, Math.floor(Number(candies?.[size] ?? 0))),
    0,
  )
}

export function wouldRegionAdvanceResetCandies(currentGeneration: number): boolean {
  return !!GENERATIONS.find((g) => g.id === currentGeneration + 1)
}

function getCompletedFrontierAdvanceTarget(
  state: Pick<PlayerState, 'currentGeneration' | 'currentZone' | 'defeatedBosses' | 'battleTowerZone'>,
): ZoneAdvanceTarget | null {
  if (state.battleTowerZone != null) return null
  const gen = GENERATIONS.find((g) => g.id === state.currentGeneration)
  const lastZone = gen?.zones[gen.zones.length - 1]
  if (!gen || !lastZone || state.currentZone !== lastZone.id) return null

  const bossKey = `g${state.currentGeneration}z${state.currentZone}`
  const paldeaChampionCleared =
    state.currentGeneration === 9 &&
    PALDEA_CHAMPION_BOSS_KEYS.some((key) => state.defeatedBosses.includes(key))
  // Paldea champion : plus de « Continuer » frontière — la tour / farm endgame prennent le relais.
  if (paldeaChampionCleared) return null

  const hasCompletedFrontierBoss =
    state.defeatedBosses.includes(bossKey) || paldeaChampionCleared

  return hasCompletedFrontierBoss
    ? { generation: state.currentGeneration, zone: state.currentZone }
    : null
}

/** Route de farm la plus avancée débloquée (aligné `completedRoutes` dans index.vue). */
export function getBestFarmRouteForProgress(
  currentGeneration: number,
  currentZone: number,
  isEndgame: boolean,
): { generation: number; zone: number } | null {
  const lastGen = GENERATIONS[GENERATIONS.length - 1]
  let best: { generation: number; zone: number } | null = null
  for (const g of GENERATIONS) {
    if (g.id > currentGeneration) break
    const entireLastRegionFarmable =
      isEndgame && lastGen !== undefined && g.id === lastGen.id
    for (const z of g.zones) {
      const farmable =
        g.id < currentGeneration || z.id < currentZone || entireLastRegionFarmable
      if (farmable) {
        best = { generation: g.id, zone: z.id }
      }
    }
  }
  return best
}

function isFarmRouteBehind(
  route: { generation: number; zone: number },
  best: { generation: number; zone: number },
): boolean {
  return (
    route.generation < best.generation ||
    (route.generation === best.generation && route.zone < best.zone)
  )
}

/** Première zone frontière dont le boss n’est pas dans `defeatedBosses` (toutes vaincues → dernière zone). */
function getFirstFrontierZoneWithPendingBoss(
  currentGeneration: number,
  defeatedBosses: string[]
): number {
  const gen = GENERATIONS.find((g) => g.id === currentGeneration)
  if (!gen?.zones?.length) return 1
  for (const z of gen.zones) {
    const key = `g${currentGeneration}z${z.id}`
    if (!defeatedBosses.includes(key)) return z.id
  }
  return gen.zones[gen.zones.length - 1]!.id
}

export const usePlayerStore = defineStore('player', {
  state: (): PlayerState => {
    const saved = typeof localStorage !== 'undefined' ? loadBonusesFromStorage() : { teamDpsBonus: 0 }
    return {
      username: '',
      serverConfirmedGold: 0,
      goldPendingLocal: 0,
      xp: 0,
      level: 1,
      currentGeneration: 1,
      currentZone: 1,
      currentStage: 1,
      stageKills: 0,
      teamDpsBonus: saved.teamDpsBonus,
      badges: 0,
      defeatedBosses: [],
      isLoggedIn: false,
      candies: { S: 0, M: 0, L: 0, XL: 0 },
      regionUnlockMessage: null,
      combatGeneration: null,
      combatZone: null,
      adminVersion: 0,
      shinyCharms: 0,
      completedPokedexGens: [],
      pokedexMaster: false,
      totalKills: 0,
      goldWellLevel: 0,
      goldWellEnabled: false,
      prestigeVaultEnabled: false,
      ascensionShinyBonusEnabled: false,
      zoneMasteryRerunAccelEnabled: false,
      ascensionAwakeningPackEnabled: false,
      ascensionRunMutatorsEnabled: true,
      ascensionTranscendenceLevel: 0,
      ascensionKeystones: [],
      ascensionSynergyEnabled: false,
      economyModifiers: { ...DEFAULT_ECONOMY_MODIFIERS },
      penaltyType: null,
      penaltyPercent: 0,
      avatarUrl: null,
      stats: {},
      showcase: [],
      achievements: [],
      selectedTitle: null,
      selectedAvatarFrame: null,
      selectedProfileBanner: null,
      selectedHudTheme: null,
      hideTeamFromProfile: false,
      supporterTier: 0,
      supporterSince: null,
      hideSupporterBadge: false,
      newInterfaceEnabled: false,
      communityUnlockedCosmeticIds: [],
      serverGoalPercentage: 0,
      chosenStarters: {},
      goldNonce: 0,
      battleTowerZone: null,
      battleTowerDefeatedTypes: [],
      battleTowerCapstoneDefeated: false,
      role: 'user',
      battleTowerLategameDisabled: false,
      tokens: 0,
      extraDaycareSlots: 0,
      offlineCapHours: OFFLINE_CAP_BASE_HOURS,
      offlineGoldEfficiencyPercent: 40,
      tokenShopFullyUnlocked: false,
      dailyQuests: [],
      runContracts: [],
      weeklyQuests: [],
      monthlyQuests: [],
      regularityState: { count: 0, milestones: [], nextMilestone: 7 },
      monthlyCalendar: { month: '', daysLogged: [], totalDays: 30 },
      casino: defaultCasinoState(),
      minigames: null,
      zoneVictoryPrompt: null,
      zoneAdvanceReady: null,
      combatAutomation: {
        bossVictoryMode: 'manual',
        autoFightBossIfBattable: false,
        autoRetreatToLastFarmRouteOnBossFailure: false,
        masteryAutoFarmMode: 'off',
        masteryAutoFarmTarget: null,
        masteryAutoFarmStopAtZoneTier: 5,
      },
      lastSelectedFarmRoute: null,
      favoriteFarmRoute: null,
      frontierReturnGuardUntil: 0,
      farmSessionSuppressedUntil: 0,
      audioPreferences: { ...DEFAULT_AUDIO_PREFERENCES },
      inclusiveWriting: false,
      invocationPity: {},
      invocationFirstShinyPity: {
        eligible: false,
        withoutShiny: 0,
        remaining: 0,
        hardMax: 1000,
      },
      descenteSanctuaireShards: 0,
      descenteSanctuaireRun: null,
      ascensionLevel: 0,
      ascensionPoints: 0,
      ascensionSpent: 0,
      ascensionUpgrades: {
        xpBoost: 0,
        goldBoost: 0,
        enemyMitigation: 0,
        starterMomentum: 0,
      },
      activeLure: null,
      offlineShinyReveal: DEFAULT_OFFLINE_SHINY_REVEAL,
    }
  },

  getters: {
    spendableGold: (state): number => state.serverConfirmedGold + state.goldPendingLocal,
    hasUnsyncedGold: (state): boolean => state.goldPendingLocal > 0,
    /** Or affiché = banque serveur + buffer farm en attente de sync. */
    gold: (state): number => state.serverConfirmedGold + state.goldPendingLocal,
    formattedGold: (state): string =>
      formatUiInteger(state.serverConfirmedGold + state.goldPendingLocal),
    formattedSpendableGold: (state): string =>
      formatUiInteger(state.serverConfirmedGold + state.goldPendingLocal),
    formattedUnsyncedGold: (state): string => formatUiInteger(state.goldPendingLocal),
    regionName: (state): string => {
      return getGenerationLabel(state.currentGeneration, localeApi().t, 'Unknown')
    },
    isBossStage: (state): boolean => {
      if (state.battleTowerZone === BATTLE_TOWER_FARM_ZONE_INDEX) {
        return false
      }
      if (state.battleTowerZone != null && state.battleTowerZone <= 18) {
        return true
      }
      if (state.combatGeneration !== null) return false
      if (state.currentStage !== STAGES_PER_ZONE) return false
      return !isPaldeaEndgameFrontierSuperseded(
        state.currentGeneration,
        state.currentZone,
        state.defeatedBosses,
        state.battleTowerZone,
        state.combatGeneration,
      )
    },
    isFarming: (state): boolean => isFarmCombatActive(state),
    zoneAdvanceTarget: (state): ZoneAdvanceTarget | null => {
      return state.zoneAdvanceReady ?? getCompletedFrontierAdvanceTarget(state)
    },
    /** Champion Paldea vaincu — même règle que le déblocage tour (`isBattleTowerUnlocked`). */
    isEndgame: (state): boolean => isBattleTowerUnlocked(state.defeatedBosses),
    needsStarterChoice: (state): boolean => {
      const gen = state.currentGeneration
      return !!getStartersForGen(gen) && !state.chosenStarters[gen]
    },
    activeCombatGen: (state): number => {
      if (isFarmSessionSuppressed(state)) return state.currentGeneration
      return state.combatGeneration ?? state.currentGeneration
    },
    activeCombatZone: (state): number => {
      if (isFarmSessionSuppressed(state)) return state.currentZone
      return state.combatZone ?? state.currentZone
    },
    /** Progression affichée (combat, profil) : tour / farm / frontière selon le contexte. */
    activeProgressLabel: (state): string => buildActiveProgressLabel(state, { farmPrefix: true }),
    stageLabel: (state): string => buildActiveProgressLabel(state),
    /** Repère campagne sauvegardé (toast « Frontière », farm). */
    highestStageLabel: (state): string => formatFrontierProgressLabel(state),
    killsPerStage: (): number => KILLS_PER_STAGE,
    stageKillsPercent(state): number {
      return Math.min(100, ((state.stageKills ?? 0) / this.combatKillsPerStage) * 100)
    },
    xpToNextLevel: (state): number => {
      return xpForLevel(state.level + 1)
    },
    xpPercent(state): number {
      const needed = xpForLevel(state.level + 1)
      const prevNeeded = xpForLevel(state.level)
      const progress = state.xp - prevNeeded
      const range = needed - prevNeeded
      return range > 0 ? Math.min(100, Math.max(0, (progress / range) * 100)) : 0
    },
    teamDpsMult(state): number {
      return 1
    },

    // ── Zone Mastery (bonus compte globaux) ───────────────────────────────────
    /** Kills sauvages cumulés sur toutes les routes. */
    accountZoneMasteryTotalKills(state): number {
      return sumZoneMasteryKillsFromStats(state.stats as Record<string, unknown>)
    },
    /** Palier compte = meilleur palier atteint sur une route (bonus partout). */
    accountZoneMasteryLevel(state): number {
      return getAccountZoneMasteryLevel(state.stats as Record<string, unknown>)
    },
    accountZoneMasteryProgress(state) {
      return getAccountZoneMasteryProgress(state.stats as Record<string, unknown>)
    },
    /** @deprecated Alias — bonus maîtrise compte (global). */
    currentZoneMasteryKills(): number {
      return (this as any).accountZoneMasteryTotalKills
    },
    currentZoneMasteryLevel(): number {
      return (this as any).accountZoneMasteryLevel
    },
    currentZoneMasteryGoldMult(): number {
      const lvl = (this as any).accountZoneMasteryLevel
      if (lvl <= 0) return 1
      return ZONE_MASTERY_BONUSES[Math.min(lvl, 5) - 1]?.goldMult ?? 1
    },
    currentZoneMasteryShinyMult(): number {
      const lvl = (this as any).accountZoneMasteryLevel
      if (lvl <= 0) return 1
      return ZONE_MASTERY_BONUSES[Math.min(lvl, 5) - 1]?.shinyMult ?? 1
    },
    currentZoneMasteryDpsMult(): number {
      const lvl = (this as any).accountZoneMasteryLevel
      if (lvl <= 0) return 1
      return ZONE_MASTERY_BONUSES[Math.min(lvl, 5) - 1]?.dpsMult ?? 1
    },
    currentZoneMasteryCandyMult(): number {
      const lvl = (this as any).accountZoneMasteryLevel
      if (lvl <= 0) return 1
      return ZONE_MASTERY_BONUSES[Math.min(lvl, 5) - 1]?.candyMult ?? 1
    },
    candyPurchaseMasteryLevel(state): number {
      return getAccountZoneMasteryLevel(state.stats as Record<string, unknown>)
    },
    /** Kills + palier pour une route précise (affichage / auto farm). */
    zoneMasteryForRoute: (state) => (generation: number, zone: number) => {
      const kills = Math.max(
        0,
        Math.floor(Number(state.stats[`kills_g${generation}z${zone}`] ?? 0)),
      )
      return { kills, level: getZoneMasteryLevel(kills) }
    },

    // ── Pokédex Bonuses ───────────────────────────────────────────────────────
    /** +10% or en combat dans la région dont le Pokédex est complété (`gen` = génération de combat). */
    pokedexGoldMultForGen: (state) => (gen: number): number => {
      return state.completedPokedexGens.includes(gen) ? 1.10 : 1.0
    },
    /** +15% DPS global si Maître Pokédex. */
    pokedexDpsMult(state): number {
      return state.pokedexMaster ? 1.15 : 1.0
    },

    // ── Lure ─────────────────────────────────────────────────────────────────
    activeLureGoldMult(state): number {
      if (!state.activeLure) return 1
      return LURE_DEFINITIONS.find((l) => l.type === state.activeLure!.type)?.goldMult ?? 1
    },
    activeLureShinyMult(state): number {
      if (!state.activeLure) return 1
      return LURE_DEFINITIONS.find((l) => l.type === state.activeLure!.type)?.shinyMult ?? 1
    },
    /** Farm secrète débloquée quand les 18 types sont cochés */
    /** Réserve Paldea (18 types) — requis pour lancer la Descente du sanctuaire. */
    descenteSanctuaireUnlocked(state): boolean {
      return (state.battleTowerDefeatedTypes?.length ?? 0) >= 18
    },
    descClearAsc0(state): number {
      return Math.max(0, Math.floor(Number((state.stats as Record<string, unknown>)?.descClearAsc0 ?? 0)))
    },
    descClearAsc1(state): number {
      return Math.max(0, Math.floor(Number((state.stats as Record<string, unknown>)?.descClearAsc1 ?? 0)))
    },
    descAscension1Unlocked(): boolean {
      return this.descClearAsc0 >= 1
    },
    descAscension2Unlocked(): boolean {
      return this.descClearAsc1 >= 1
    },

    battleTowerFarmUnlocked(state): boolean {
      const need = 18
      return (state.battleTowerDefeatedTypes?.length ?? 0) >= need
    },
    goldBonusMult(state): number {
      return 1
    },
    ascensionPostBoostMult(state): number {
      const until = Math.max(0, Math.floor(Number((state.stats as Record<string, unknown>)?._ascPostBoostUntil ?? 0)))
      return until > Date.now() ? ASCENSION_POST_BOOST_MULT : 1
    },
    ascensionGoldBurstMult(state): number {
      if (!state.ascensionAwakeningPackEnabled) return 1
      const until = Math.max(0, Math.floor(Number((state.stats as Record<string, unknown>)?._ascBurstGoldUntil ?? 0)))
      const mult = Number((state.stats as Record<string, unknown>)?._ascBurstGoldMult ?? 2)
      return until > Date.now() && Number.isFinite(mult) && mult > 1 ? mult : 1
    },
    ascensionRunMutator(state): AscensionRunMutatorId {
      // Kill-switch étanche : feature désactivée ⇒ aucun effet, même si un mutateur
      // résiduel traîne dans les stats (activation puis désactivation du flag).
      if (!state.ascensionRunMutatorsEnabled) return 'none'
      return normalizeAscensionRunMutatorId((state.stats as Record<string, unknown>)?._ascRunMutator)
    },
    ascensionRunMutatorEffect(): ReturnType<typeof getAscensionRunMutatorEffect> {
      const inventory = useInventoryStore()
      const firstMember = inventory.team[0]
      const firstType = firstMember ? getPokemonTypes(firstMember)[0] : undefined
      const monoTypeTeam =
        firstType != null &&
        inventory.team.length > 0 &&
        inventory.team.every((p) => getPokemonTypes(p).includes(firstType))
      return getAscensionRunMutatorEffect(this.ascensionRunMutator, { monoTypeTeam })
    },
    ascensionNgShinyMult(state): number {
      if (!state.ascensionShinyBonusEnabled || state.ascensionLevel <= 0) return 1
      return getAscensionShinyMult(state.ascensionLevel)
    },
    ascensionXpMult(state): number {
      return roundAscensionMultiplier(
        (1 + Math.max(0, state.ascensionUpgrades.xpBoost) * ASCENSION_XP_STEP)
          * this.ascensionPostBoostMult
          * this.ascensionRunMutatorEffect.xpMult
      )
    },
    /**
     * Multiplicateur or d'ascension appliqué **en plus** de `ascensionRewardMult`.
     *
     * Historiquement le rang `goldBoost` compte dans les deux (×6 au lieu de ×3 à
     * 20 rangs) : `ascensionGoldBoostDoubleCount = false` supprime ce double comptage
     * sans toucher au reste de la courbe.
     */
    ascensionGoldMult(state): number {
      const mods = state.economyModifiers
      const goldBoostPart = mods.ascensionGoldBoostDoubleCount
        ? 1 + Math.max(0, state.ascensionUpgrades.goldBoost) * ASCENSION_GOLD_STEP
        : 1
      return roundAscensionMultiplier(
        applyAscensionGoldCurve(
          goldBoostPart
            * this.ascensionPostBoostMult
            * this.ascensionGoldBurstMult
            * this.ascensionRunMutatorEffect.goldMult,
          mods,
        )
      )
    },
    ascensionEnemyHpMult(state): number {
      return roundAscensionMultiplier(
        getRouteEnemyHpMult(
          state.ascensionLevel,
          Math.max(0, state.ascensionUpgrades.enemyMitigation),
        ) * this.ascensionRunMutatorEffect.enemyHpMult
      )
    },
    /** Mult. PV boss tour (scaling NG+ modéré, aligné `shared/ascension_balance`). */
    battleTowerBossHpMult(state): number {
      return getBattleTowerBossHpMult(
        state.ascensionLevel,
        Math.max(0, state.ascensionUpgrades.enemyMitigation),
      )
    },
    ascensionUnlockKills(state): number {
      return getAscensionUnlockKills(state.ascensionLevel)
    },
    /**
     * Multiplicateur de récompenses NG+ — sert à l'or **et** à l'XP / aux bases de la tour.
     * Ne pas y appliquer la courbe or : utiliser `ascensionRewardGoldMult` pour l'or.
     */
    ascensionRewardMult(state): number {
      const effectivePowerLevel = Math.min(ASCENSION_POWER_LEVEL_CAP, Math.max(0, state.ascensionLevel))
      return roundAscensionMultiplier(
        (1
          + effectivePowerLevel * ASCENSION_POWER_STEP
          + Math.max(0, state.ascensionUpgrades.goldBoost) * ASCENSION_REWARD_GOLD_STEP)
          * this.ascensionRunMutatorEffect.rewardMult
      )
    },
    /** Variante or-seulement de `ascensionRewardMult` (courbe d'ascension admin appliquée). */
    ascensionRewardGoldMult(state): number {
      return roundAscensionMultiplier(
        applyAscensionGoldCurve(this.ascensionRewardMult, state.economyModifiers)
      )
    },
    /**
     * Borne la pile multiplicative d'or en combat selon `goldStackCap`.
     * Exposé en getter (et non calculé ici) parce que la pile complète inclut la
     * synergie d'équipe, qui vit dans un composable : un seul point d'assemblage
     * dans `useCombatLoop` évite que les deux versions divergent.
     */
    capCombatGoldStack(state) {
      return (stack: number): number => capGoldStack(stack, state.economyModifiers)
    },
    ascensionDpsMult(state): number {
      return roundAscensionMultiplier(
        1 + Math.max(0, state.ascensionUpgrades.starterMomentum) * ASCENSION_DPS_STEP
      )
    },
    /** Nombre de générations Pokédex complétées qui amplifient les synergies (0 si flag off). */
    synergyGenCount(state): number {
      return state.ascensionSynergyEnabled ? (state.completedPokedexGens?.length ?? 0) : 0
    },
    /** Bonus DPS du Puits à or (permanent), gated sur le flag serveur + 1ère ascension. Amplifié par le Pokédex (T3). */
    goldWellDpsMult(state): number {
      if (!state.goldWellEnabled || state.ascensionLevel <= 0) return 1
      return getGoldWellDpsMult(state.goldWellLevel, this.synergyGenCount)
    },
    /** Bonus DPS de la Transcendance (sink infini), permanent. Amplifié par le Pokédex (T3). */
    ascensionTranscendenceDpsMult(state): number {
      return getAscensionTranscendenceDpsMult(state.ascensionTranscendenceLevel, this.synergyGenCount)
    },
    /** Effet combiné des keystones attunés (DPS / or / shiny / PV ennemis), pour l'UI atelier. */
    keystoneEffect(state): AscensionKeystoneEffect {
      if (getAscensionKeystoneSlots(state.ascensionLevel) <= 0) {
        return getCombinedKeystoneEffect([])
      }
      return getCombinedKeystoneEffect(state.ascensionKeystones ?? [])
    },
    /**
     * Effet keystone *appliqué en combat*. Actif partout, **y compris Battle Tower** (2026-06) :
     * les builds comptent aussi en tour. Le malus PV (glass cannon) s'applique donc aux boss
     * tour (spawn + readiness) pour rester cohérent.
     */
    combatKeystoneEffect(state): AscensionKeystoneEffect {
      if (getAscensionKeystoneSlots(state.ascensionLevel) <= 0) {
        return getCombinedKeystoneEffect([])
      }
      return getCombinedKeystoneEffect(state.ascensionKeystones ?? [])
    },
    /** Kills sauvages requis par palier de frontière (keystones + maîtrise rerun + élan NG+). */
    combatKillsPerStage(): number {
      let mult = (this.combatKeystoneEffect as AscensionKeystoneEffect).killsPerStageMult
      if (this.zoneMasteryRerunAccelEnabled && this.battleTowerZone == null) {
        mult *= getZoneMasteryKillsPerStageMult(this.currentZoneMasteryLevel)
      }
      if (
        this.ascensionAwakeningPackEnabled
        && this.battleTowerZone == null
        && isAscensionRouteSurgeActive(
          Number((this.stats as Record<string, unknown>)?._ascRouteSurgeUntil ?? 0),
          this.currentGeneration,
        )
      ) {
        mult *= ASCENSION_ROUTE_SURGE_KILLS_MULT
      }
      return Math.max(1, Math.ceil(KILLS_PER_STAGE * mult))
    },
    /** Mult. coût Puits à or (keystone banquier) — déverrouillé après la 1ère ascension. */
    goldWellKeystoneCostMult(state): number {
      if (getAscensionKeystoneSlots(state.ascensionLevel) <= 0) return 1
      return getCombinedKeystoneEffect(state.ascensionKeystones ?? []).goldWellCostMult
    },
    /** Malus DPS combat (routes / boss normaux) lié au niveau d'ascension. */
    ascensionCombatPressureMult(state): number {
      return getAscensionCombatPressureMult(state.ascensionLevel, false)
    },
    /** Pression NG+ en combat : pleine en routes, fraction en tour (aligné `shared/ascension_balance`). */
    combatAscensionPressureMult(): number {
      return getAscensionCombatPressureMult(
        this.ascensionLevel,
        this.battleTowerZone != null,
      )
    },
  },

  actions: {
    /**
     * Applique les modificateurs d'économie reçus du serveur.
     *
     * Deux destinations, volontairement :
     *  - `state.economyModifiers` : réactif, pour que les prix affichés (boutique,
     *    gacha, puits) se recalculent immédiatement ;
     *  - registre ambiant partagé : lu par les fonctions pures d'économie appelées
     *    hors contexte réactif (boucle de combat, estimation offline).
     *
     * Appelé au `/load` (`features.economy`) puis à chaque push WS `economy:update`.
     */
    applyEconomyModifiers(incoming: unknown) {
      const next = normalizeEconomyModifiers(
        (incoming ?? null) as Partial<Record<keyof EconomyModifiers, unknown>> | null,
      )
      this.economyModifiers = next
      setEconomyModifiers(next)
    },

    advanceToNextZoneOrGeneration() {
      const gen = GENERATIONS.find((g) => g.id === this.currentGeneration)
      if (!gen) return

      // Check if there are more zones in the current generation
      if (this.currentZone >= gen.zones.length) {
        // Move to next generation
        const nextGen = GENERATIONS.find((g) => g.id === this.currentGeneration + 1)
        if (nextGen) {
          this.currentGeneration++
          this.currentZone = 1
          // Reset candies on region unlock (prevents stockpiling cheap candies)
          for (const k of ['S', 'M', 'L', 'XL'] as CandySize[]) {
            this.candies[k] = 0
          }
          const newRegionName = getGenerationLabel(this.currentGeneration, localeApi().t, 'Unknown')
          this.regionUnlockMessage = `🎉 Félicitations ! Vous avez débloqué la région ${newRegionName} !`
        } else {
          // Dernière région : pas de farm auto sur Paldea — suite endgame optionnelle
          this.currentZone = gen.zones.length
          this.combatGeneration = null
          this.combatZone = null
          const canEnterTower =
            !this.battleTowerLategameDisabled || this.role === 'admin'
          if (canEnterTower) {
            this.battleTowerZone = getNextBattleTowerZoneIndex(
              this.battleTowerDefeatedTypes,
              this.battleTowerCapstoneDefeated,
            )
          }
          // Ne pas réafficher la modale si le champion est déjà enregistré (ex. retour tour étage 1).
          if (!isBattleTowerUnlocked(this.defeatedBosses)) {
            this.regionUnlockMessage =
              `🏆 Champion vaincu ! Explore Paldea encore : quelque chose d’inédit t’attend peut‑être sur la route…`
          }
        }
      } else {
        this.currentZone++
      }
      this.syncDefaultFarmRouteToProgress()
    },

    /** Met à jour la route de farm mémorisée vers la route la plus récente débloquée. */
    syncDefaultFarmRouteToProgress() {
      if (this.battleTowerZone != null) return
      const route = getBestFarmRouteForProgress(
        this.currentGeneration,
        this.currentZone,
        isBattleTowerUnlocked(this.defeatedBosses),
      )
      if (route) {
        this.lastSelectedFarmRoute = route
      }
    },

    /**
     * Au chargement : rattrape une route de farm en retard sur la frontière (sauvegarde partielle).
     * Ne rétrograde jamais un choix manuel vers une route plus ancienne.
     */
    reconcileFarmRouteWithProgress() {
      if (this.battleTowerZone != null) return
      const best = getBestFarmRouteForProgress(
        this.currentGeneration,
        this.currentZone,
        isBattleTowerUnlocked(this.defeatedBosses),
      )
      if (!best) return
      if (
        !this.lastSelectedFarmRoute ||
        isFarmRouteBehind(this.lastSelectedFarmRoute, best)
      ) {
        this.lastSelectedFarmRoute = best
      }
      // Ne pas forcer le combat vers la route « max » : un farm volontaire sur une
      // route plus ancienne (ex. route 1) doit survivre aux reload / retours d’onglet.
    },

    goToNextZone() {
      const zvp = this.zoneVictoryPrompt
      const gcf = getCompletedFrontierAdvanceTarget(this)
      const zar = this.zoneAdvanceReady
      // Champion / modale d’abord — sinon un `zoneAdvanceReady` résiduel masquait `gcf` et permettait
      // un avancement sans badge (ex. après ascension + reload partiel du store).
      const pendingAdvance = zvp ?? gcf ?? zar
      if (!pendingAdvance) return

      if (this.battleTowerZone != null) {
        this.zoneVictoryPrompt = null
        this.zoneAdvanceReady = null
        return
      }
      if (
        !zvp &&
        !gcf &&
        zar &&
        this.battleTowerZone == null &&
        this.combatGeneration == null
      ) {
        const bossKey = `g${this.currentGeneration}z${this.currentZone}`
        if (!this.defeatedBosses.includes(bossKey)) {
          this.zoneAdvanceReady = null
          return
        }
      }
      this.zoneVictoryPrompt = null
      this.zoneAdvanceReady = null
      // En quittant un mode farm "resté dans la zone", on revient sur la frontière.
      this.combatGeneration = null
      this.combatZone = null
      this.advanceToNextZoneOrGeneration()
      this.currentStage = 1
      this.stageKills = 0
    },

    stayInCurrentZone() {
      // Le joueur garde sa progression dans la zone actuelle après victoire boss.
      this.zoneAdvanceReady = this.zoneVictoryPrompt ?? this.zoneAdvanceReady
      this.zoneVictoryPrompt = null
      this.currentStage = 1
      this.stageKills = 0
      // Le maître étant déjà vaincu, on bascule sur la route en mode farm (pas de re-boss).
      this.travelTo(this.currentGeneration, this.currentZone)
    },

    /** Réouvre la modale victoire si un « Continuer » risquerait d’effacer des bonbons sans avertissement. */
    reopenZoneVictoryPromptIfCandyLossRisk(): boolean {
      if (this.zoneVictoryPrompt) return false
      if (!this.zoneAdvanceTarget) return false
      if (!wouldRegionAdvanceResetCandies(this.currentGeneration)) return false
      if (getCandyTotalPieces(this.candies) <= 0) return false
      this.zoneVictoryPrompt = {
        generation: this.currentGeneration,
        zone: this.currentZone,
      }
      return true
    },

    resolveZoneVictoryAutomation() {
      if (!this.zoneVictoryPrompt) return false
      if (this.combatAutomation.bossVictoryMode === 'stay') {
        this.stayInCurrentZone()
        return true
      }
      if (this.combatAutomation.bossVictoryMode === 'continue') {
        // Ne pas avancer automatiquement si des bonbons seraient perdus — la modale reste visible.
        if (
          wouldRegionAdvanceResetCandies(this.currentGeneration) &&
          getCandyTotalPieces(this.candies) > 0
        ) {
          return false
        }
        this.goToNextZone()
        return true
      }
      return false
    },

    // Migration: rename legacy duplicate boss slugs (koga/bruno/lance appear in both Gen1 and Gen2)
    // Old saves stored only 'koga', 'bruno', 'lance' — these were Gen1 bosses.
    // Gen2 versions now use 'koga-gen2', 'bruno-gen2', 'lance-gen2'.
    // If player progressed past the Gen2 zone, infer the gen2 slug too.
    migrateBossSlugs() {
      const renames: Array<{ old: string; gen2: string; gen2ZoneId: number }> = [
        { old: 'koga', gen2: 'koga-gen2', gen2ZoneId: 10 },
        { old: 'bruno', gen2: 'bruno-gen2', gen2ZoneId: 11 },
        { old: 'lance', gen2: 'lance-gen2', gen2ZoneId: 13 },
      ]
      let changed = false
      for (const r of renames) {
        // If player already has the gen2 slug, skip
        if (this.defeatedBosses.includes(r.gen2)) continue
        // If player has the old slug (Gen1), check if they've also beaten the Gen2 version
        // Gen2 = genId 2. If currentGen > 2, or currentGen === 2 && currentZone > gen2ZoneId, they beat it
        if (this.currentGeneration > 2 || (this.currentGeneration === 2 && this.currentZone > r.gen2ZoneId)) {
          this.defeatedBosses.push(r.gen2)
          changed = true
        }
      }
      return changed
    },

    /** @deprecated Utiliser `accrueCombatGold` (crédit serveur par batch). */
    addGold(_amount: number) {
      /* no-op : farm via useCombatGoldCredit */
    },

    /**
     * Banque serveur confirmée. Ne touche PAS `goldPendingLocal` (buffer farm) —
     * réaligner via `syncAccrualRef` / `setCombatGoldAccrualBuffer` côté crédit combat.
     */
    applyWalletFromServer(gold: number, goldNonce?: number) {
      this.serverConfirmedGold = Math.max(0, Math.floor(gold))
      if (typeof goldNonce === 'number') {
        this.goldNonce = goldNonce
      }
    },

    spendGold(amount: number): boolean {
      const total = this.serverConfirmedGold + this.goldPendingLocal
      if (total < amount) return false
      let remaining = Math.max(0, Math.floor(amount))
      if (this.serverConfirmedGold >= remaining) {
        this.serverConfirmedGold -= remaining
      } else {
        remaining -= this.serverConfirmedGold
        this.serverConfirmedGold = 0
        this.goldPendingLocal = Math.max(0, this.goldPendingLocal - remaining)
        void import('~/composables/useCombatGoldCredit').then(({ setCombatGoldAccrualBuffer }) =>
          setCombatGoldAccrualBuffer(this.goldPendingLocal),
        )
      }
      this.addStat('goldSpent', amount)
      return true
    },

    /**
     * Réponse POST /game/save : ne pas écraser tout l’affichage si l’or n’a pas été écrit (nonce).
     * Si l’or a été appliqué, le serveur a la vérité (plafond anti-triche inclus).
     */
    applySaveGoldResponse(saveResult: {
      gold?: number
      goldNonce?: number
      goldApplied?: boolean
    }) {
      if (saveResult.goldApplied === false) return
      if (saveResult.gold !== undefined) {
        this.applyWalletFromServer(saveResult.gold, saveResult.goldNonce)
      } else if (saveResult.goldNonce !== undefined) {
        this.goldNonce = saveResult.goldNonce
      }
    },

    /** Solde serveur après gacha / casino / autre écriture autoritaire. */
    setGoldFromServerAuthoritative(total: number, goldNonce?: number) {
      this.applyWalletFromServer(total, goldNonce)
    },

    /** Après erreur gacha + reload : garder l’affichage si le reload était trop bas (sans contredire « pas assez d’or »). */
    ensureGoldDisplayAtLeast(minTotal: number) {
      if (minTotal > this.serverConfirmedGold) {
        this.serverConfirmedGold = minTotal
      }
    },
    
    spendTokens(amount: number): boolean {
      if ((this.tokens ?? 0) < amount) return false
      this.tokens -= amount
      return true
    },

    addXp(amount: number) {
      // Au niveau max, l'XP ne sert plus : sans plafond elle dépasse la borne du validateur
      // serveur et toutes les sauvegardes / farm-sync partent en 422.
      this.xp = Math.min(this.xp + amount, xpForLevel(MAX_PLAYER_LEVEL))
      while (this.level < MAX_PLAYER_LEVEL && this.xp >= xpForLevel(this.level + 1)) {
        this.level++
      }
    },

    setAscensionState(raw: {
      level?: number
      points?: number
      spent?: number
      upgrades?: Partial<{
        xpBoost: number
        goldBoost: number
        enemyMitigation: number
        starterMomentum: number
      }>
    } | null | undefined) {
      this.ascensionLevel = Math.max(0, Math.floor(Number(raw?.level ?? 0)))
      this.ascensionPoints = Math.max(0, Math.floor(Number(raw?.points ?? 0)))
      this.ascensionSpent = Math.max(0, Math.floor(Number(raw?.spent ?? 0)))
      this.ascensionUpgrades = {
        xpBoost: Math.max(0, Math.floor(Number(raw?.upgrades?.xpBoost ?? 0))),
        goldBoost: Math.max(0, Math.floor(Number(raw?.upgrades?.goldBoost ?? 0))),
        enemyMitigation: Math.max(0, Math.floor(Number(raw?.upgrades?.enemyMitigation ?? 0))),
        starterMomentum: Math.max(0, Math.floor(Number(raw?.upgrades?.starterMomentum ?? 0))),
      }
    },

    /** Modales client éphémères — ne doivent pas survivre à un reload / reset de run. */
    clearEphemeralRunUiState() {
      this.regionUnlockMessage = null
      this.zoneVictoryPrompt = null
      this.zoneAdvanceReady = null
    },

    /**
     * Alignement client immédiat après ascension (avant GET /load).
     * Ne touche pas ascension meta, maîtrise de zone ni cosmétiques lifetime.
     */
    applyLocalNgPlusRunShell() {
      this.clearEphemeralRunUiState()
      this.chosenStarters = {}
      this.serverConfirmedGold = 0
      this.goldPendingLocal = 0
      this.xp = 0
      this.level = 1
      this.badges = 0
      this.currentGeneration = 1
      this.currentZone = 1
      this.currentStage = 1
      this.stageKills = 0
      this.teamDpsBonus = 0
      this.totalKills = 0
      this.candies = { S: 0, M: 0, L: 0, XL: 0 }
      this.shinyCharms = 0
      this.defeatedBosses = []
      this.completedPokedexGens = []
      this.pokedexMaster = false
      this.battleTowerDefeatedTypes = []
      this.battleTowerZone = null
      this.battleTowerCapstoneDefeated = false
      this.combatGeneration = null
      this.combatZone = null
      this.farmSessionSuppressedUntil = 0
      this.frontierReturnGuardUntil = 0
      this.lastSelectedFarmRoute = null
      this.favoriteFarmRoute = null
      this.descenteSanctuaireRun = null
    },

    addStageKill(): boolean {
      this.stageKills = (this.stageKills ?? 0) + 1
      if (this.stageKills >= this.combatKillsPerStage) {
        this.stageKills = 0
        this.advanceStage()
        return true
      }
      return false
    },

    advanceQuestProgress(metric: QuestProgressMetric, amount: number) {
      const delta = Math.max(0, Math.floor(Number(amount) || 0))
      if (delta <= 0) return

      const apply = (quests: ProgressQuestState[]) => {
        for (const quest of quests) {
          if (quest.metric !== metric || quest.claimed) continue
          quest.progress = Math.min(
            quest.target,
            Math.max(0, Math.floor(Number(quest.progress ?? 0))) + delta
          )
          if (quest.progress >= quest.target) quest.completed = true
        }
      }

      // Quêtes journalières / contrats : progression live via useQuestLiveProgress (évite double comptage).
      apply(this.weeklyQuests)
      apply(this.monthlyQuests)
    },

    recordKill() {
      this.totalKills = Math.max(0, Math.floor(Number(this.totalKills ?? 0))) + 1
      this.advanceQuestProgress('totalKills', 1)
    },

    mergeTimedQuestProgress(
      currentQuests: WeeklyQuestState[],
      serverQuests: WeeklyQuestState[]
    ): WeeklyQuestState[] {
      const currentById = new Map(currentQuests.map((quest) => [quest.id, quest]))

      return serverQuests.map((serverQuest) => {
        const currentQuest = currentById.get(serverQuest.id)
        if (!currentQuest || currentQuest.resetsAtMs !== serverQuest.resetsAtMs) {
          return serverQuest
        }

        const progress = Math.max(
          Math.max(0, Math.floor(Number(serverQuest.progress ?? 0))),
          Math.max(0, Math.floor(Number(currentQuest.progress ?? 0)))
        )

        return {
          ...serverQuest,
          progress: Math.min(serverQuest.target, progress),
          completed: serverQuest.completed || currentQuest.completed || progress >= serverQuest.target,
        }
      })
    },

    applyWeeklyQuestsFromServer(quests: WeeklyQuestState[]) {
      this.weeklyQuests = this.mergeTimedQuestProgress(this.weeklyQuests, quests)
    },

    applyMonthlyQuestsFromServer(quests: WeeklyQuestState[]) {
      this.monthlyQuests = this.mergeTimedQuestProgress(this.monthlyQuests, quests)
    },

    advanceStage() {
      if (this.currentStage < STAGES_PER_ZONE) {
        this.currentStage++
      } else {
        this.currentStage = 1

        // Add badge only if boss is defeated for the first time
        // Use gen:zone key (e.g. "g1z12") instead of pokemon slug to avoid duplicates
        const gen = GENERATIONS.find((g) => g.id === this.currentGeneration)
        const bossKey = gen ? `g${this.currentGeneration}z${this.currentZone}` : null
        const isFirstKill = bossKey ? !this.defeatedBosses.includes(bossKey) : false

        if (bossKey && isFirstKill) {
          this.defeatedBosses.push(bossKey)
          this.badges++
        }

        // La modale "Rester / Continuer" ne s'affiche que sur le dernier boss de la zone frontière
        // (fin de région / passage majeur), pas sur chaque route intermédiaire.
        const isFrontierProgression = this.combatGeneration === null && this.battleTowerZone == null
        const isLastZoneBoss = !!gen && this.currentZone >= gen.zones.length
        if (isFrontierProgression && isFirstKill && isLastZoneBoss) {
          this.zoneVictoryPrompt = {
            generation: this.currentGeneration,
            zone: this.currentZone,
          }
          this.currentStage = 1
          this.stageKills = 0
          return
        }
        // Si le boss final de zone est déjà validé (ex: après "Rester"), ne jamais auto-pass à la région suivante.
        // Le passage reste piloté par `zoneAdvanceReady` + bouton "Continuer l'aventure".
        if (isFrontierProgression && !isFirstKill && isLastZoneBoss) {
          if (
            isPaldeaEndgameFrontierSuperseded(
              this.currentGeneration,
              this.currentZone,
              this.defeatedBosses,
              this.battleTowerZone,
              this.combatGeneration,
            )
          ) {
            const canTower = !this.battleTowerLategameDisabled || this.role === 'admin'
            if (canTower) {
              this.enterBattleTowerAtProgress()
            }
            this.currentStage = 1
            this.stageKills = 0
            return
          }
          this.zoneAdvanceReady = this.zoneAdvanceReady ?? getCompletedFrontierAdvanceTarget(this)
          this.currentStage = 1
          this.stageKills = 0
          return
        }

        this.advanceToNextZoneOrGeneration()
      }
      this.stageKills = 0
    },

    retreatStage() {
      if (this.currentStage > 1) {
        this.currentStage--
      }
      this.stageKills = 0
    },

    travelTo(gen: number, zone: number) {
      this.farmSessionSuppressedUntil = 0
      this.combatGeneration = gen
      this.combatZone = zone
      this.stageKills = 0
      this.lastSelectedFarmRoute = { generation: gen, zone }
    },

    goToFavoriteFarmRoute(): boolean {
      const route = this.favoriteFarmRoute
      if (!route) return false
      this.travelTo(route.generation, route.zone)
      return true
    },

    setFavoriteFarmRoute(route: { generation: number; zone: number } | null) {
      this.favoriteFarmRoute = route
    },

    clearFavoriteFarmRoute() {
      this.favoriteFarmRoute = null
    },

    setCombatAutomation(settings: Partial<PlayerState['combatAutomation']>) {
      this.combatAutomation = {
        ...this.combatAutomation,
        ...settings,
      }
    },

    setOfflineShinyRevealPreference(value: OfflineShinyRevealPreference) {
      this.offlineShinyReveal = normalizeOfflineShinyRevealPreference(value)
    },

    setAudioPreferences(next: Partial<AudioPreferences>) {
      this.audioPreferences = normalizeAudioPreferences({
        ...this.audioPreferences,
        ...next,
      })
    },

    setInclusiveWriting(enabled: boolean) {
      this.inclusiveWriting = Boolean(enabled)
    },

    isFrontierReturnGuarded(): boolean {
      return Date.now() < (this.frontierReturnGuardUntil ?? 0)
    },

    /** Quitte explicitement le mode farm (coords combat ≠ progression). */
    exitFarmMode() {
      // Bloque la ré-hydratation serveur du farm jusqu’à un choix explicite de route (`travelTo`).
      this.frontierReturnGuardUntil = FARM_SUPPRESSION_HOLD_UNTIL
      this.farmSessionSuppressedUntil = FARM_SUPPRESSION_HOLD_UNTIL
      this.combatGeneration = null
      this.combatZone = null
      this.stageKills = 0
    },

    /** Maintient le blocage farm (porte boss, retour frontière). */
    extendFarmSessionSuppression() {
      this.farmSessionSuppressedUntil = FARM_SUPPRESSION_HOLD_UNTIL
      this.frontierReturnGuardUntil = FARM_SUPPRESSION_HOLD_UNTIL
    },

    /**
     * Applique la zone de farm depuis le serveur sauf pendant un retour frontière explicite
     * (évite qu’un /load ou offline-report ne réactive le farm juste après le clic).
     */
    applyCombatFarmFromServer(cg: unknown, cz: unknown) {
      if (this.isFrontierReturnGuarded() || isFarmSessionSuppressed(this)) {
        this.combatGeneration = null
        this.combatZone = null
        return
      }
      if (cg != null && cz != null) {
        this.farmSessionSuppressedUntil = 0
        this.combatGeneration = Math.floor(Number(cg))
        this.combatZone = Math.floor(Number(cz))
      } else {
        this.combatGeneration = null
        this.combatZone = null
      }
    },

    handleNormalBossFailure(): boolean {
      if (this.combatAutomation.autoRetreatToLastFarmRouteOnBossFailure && this.goToLastSelectedFarmRoute()) {
        return true
      }
      // Déjà à l’étape 1 : ne pas reculer davantage (évite 9/10 → 8/10 en double repli).
      if (this.currentStage <= 1) return false
      this.retreatStage()
      return false
    },

    goToLastSelectedFarmRoute(): boolean {
      const route = this.lastSelectedFarmRoute
      if (!route) return false
      // Choix explicite de farm (repli boss) : lever le garde « reprise frontière ».
      this.frontierReturnGuardUntil = 0
      this.farmSessionSuppressedUntil = 0
      this.combatGeneration = route.generation
      this.combatZone = route.zone
      this.stageKills = 0
      return true
    },

    /**
     * Après une absence offline : reprendre la route de farm mémorisée si le serveur
     * n’a pas réhydraté les coords combat (sinon retour sur l’étape boss de la frontière).
     */
    tryRestoreFarmRouteAfterOfflineReturn(opts: { offlineReportPending: boolean }): boolean {
      if (!opts.offlineReportPending) return false
      if (this.isFrontierReturnGuarded() || isFarmSessionSuppressed(this)) return false
      if (this.battleTowerZone != null) return false
      if (this.combatGeneration !== null) return false
      return this.goToLastSelectedFarmRoute()
    },

    returnToFrontier(opts?: { enterTowerIfUnlocked?: boolean }) {
      this.exitFarmMode()
      // Après un farm, ne pas rester sur l’étape boss si ce boss est déjà vaincu (sinon re-combat du champion)
      const gen = GENERATIONS.find((g) => g.id === this.currentGeneration)
      const bossKey = gen ? `g${this.currentGeneration}z${this.currentZone}` : null
      const lastZoneId = gen?.zones[gen.zones.length - 1]?.id
      const onLastZoneOfGen =
        gen !== undefined && lastZoneId !== undefined && this.currentZone === lastZoneId
      // Paldea champion : clés g9z13 ou geeta (équivalentes) — sinon étape 10 + retour farm = Alisma à refaire
      const championPaldeaCleared =
        this.currentGeneration === 9 && onLastZoneOfGen && isBattleTowerUnlocked(this.defeatedBosses)
      const bossAlreadyCleared =
        (bossKey && this.defeatedBosses.includes(bossKey)) || championPaldeaCleared
      if (bossAlreadyCleared) {
        this.currentStage = 1
      }
      // Champion Paldea battu : la tour remplace la frontière classique (bouton dédié en endgame).
      const enterTower = opts?.enterTowerIfUnlocked !== false
      const canTower = !this.battleTowerLategameDisabled || this.role === 'admin'
      if (enterTower && isBattleTowerUnlocked(this.defeatedBosses) && canTower) {
        this.enterBattleTowerAtProgress()
      } else if (!isBattleTowerUnlocked(this.defeatedBosses)) {
        this.leaveBattleTower()
      }
      // Endgame : si la tour est désactivée globalement, on reste sur la frontière sans boucler le boss (isBossStage gère le reste).
    },

    buyCandy(size: CandySize, qty: number = 1): boolean {
      const cost = getCandyCost(size, this.currentGeneration, this.economyModifiers) * qty
      if (!this.spendGold(cost)) return false
      const received = getZoneMasteryCandyQtyPurchased(qty, (this as any).candyPurchaseMasteryLevel)
      this.candies[size] = (this.candies[size] || 0) + received
      return true
    },

    /** Annule un achat de bonbons suite à un échec réseau : retire les bonbons et restaure l’or. */
    revertBuyCandy(size: CandySize, qtyReceived: number, cost: number) {
      this.candies[size] = Math.max(0, (this.candies[size] || 0) - qtyReceived)
      this.serverConfirmedGold += cost
      this.addStat('goldSpent', -cost)
    },

    /**
     * Réconcilie les bonbons après un achat serveur autoritaire.
     * Le serveur inclut le bonus maîtrise ; on applique sa vérité (flush bonbons avant achat).
     */
    reconcileCandiesAfterPurchase(serverCandies: Partial<Record<CandySize, number>>) {
      for (const k of ['S', 'M', 'L', 'XL'] as CandySize[]) {
        if (serverCandies[k] === undefined) continue
        this.candies[k] = Math.max(0, Math.floor(Number(serverCandies[k] ?? 0)))
      }
    },

    useCandy(size: CandySize, qty: number = 1): boolean {
      const amount = Math.max(1, Math.floor(qty))
      const count = this.candies[size] || 0
      if (count < amount) return false
      this.candies[size] = count - amount
      this.addStat('candiesUsed', amount)
      return true
    },

    // ── Lure helpers ──
    buyLure(type: LureType): boolean {
      const def = LURE_DEFINITIONS.find((l) => l.type === type)
      if (!def) return false
      if ((this.tokens ?? 0) < def.costTokens) return false
      this.tokens -= def.costTokens
      this.activeLure = { type, killsRemaining: def.killDuration }
      return true
    },

    consumeLureKill() {
      if (!this.activeLure) return
      this.activeLure.killsRemaining--
      if (this.activeLure.killsRemaining <= 0) {
        this.activeLure = null
      }
    },

    // ── Stats helpers ──
    addStat(key: string, amount: number) {
      if (!this.stats) this.stats = {}
      this.stats[key] = (this.stats[key] ?? 0) + amount
      if (key === 'totalDamage' || key === 'totalSummons') {
        this.advanceQuestProgress(key, amount)
      }
    },

    setStat(key: string, value: number) {
      if (!this.stats) this.stats = {}
      this.stats[key] = value
    },

    saveBonuses() {
      saveBonusesToStorage(this.teamDpsBonus)
    },

    /** Retourne le nombre de clés `g*z*` ajoutées (réparation progression / badges). */
    setPlayer(
      data: Partial<PlayerState> & { gold?: number; offlineShopProgress?: OfflineShopProgress },
    ): number {
      const previousAudioPreferences = this.audioPreferences
      const incomingAudioPreferences = (data as any).audioPreferences
      const incomingInclusiveWriting = (data as Partial<PlayerState>).inclusiveWriting
      const { gold: loadGold, audioPreferences: _incomingAudioPreferences, inclusiveWriting: _iw, ...rest } = data as any
      Object.assign(this, rest)
      if (loadGold !== undefined) {
        this.serverConfirmedGold = loadGold
        this.goldPendingLocal = 0
      }
      // Ensure candies always has all keys with numeric values
      for (const k of ['S', 'M', 'L', 'XL'] as CandySize[]) {
        if (typeof this.candies[k] !== 'number' || isNaN(this.candies[k])) this.candies[k] = 0
      }
      // Ensure new fields have safe defaults (old saves may not include them)
      if (typeof this.shinyCharms !== 'number' || isNaN(this.shinyCharms)) this.shinyCharms = 0
      if (!Array.isArray(this.completedPokedexGens)) this.completedPokedexGens = []
      this.pokedexMaster = POKEDEX_MASTER_GEN_IDS.every((g) => this.completedPokedexGens.includes(g))
      if (!this.stats || typeof this.stats !== 'object') this.stats = {}
      if (!Array.isArray(this.showcase)) this.showcase = []
      if (!Array.isArray(this.achievements)) this.achievements = []
      if (typeof this.selectedTitle !== 'string' || this.selectedTitle.length === 0) {
        this.selectedTitle = null
      }
      if (typeof this.selectedAvatarFrame !== 'string' || this.selectedAvatarFrame.length === 0) {
        this.selectedAvatarFrame = null
      }
      if (typeof this.selectedProfileBanner !== 'string' || this.selectedProfileBanner.length === 0) {
        this.selectedProfileBanner = null
      }
      if (typeof this.selectedHudTheme !== 'string' || this.selectedHudTheme.length === 0) {
        this.selectedHudTheme = null
      }
      if (typeof this.hideTeamFromProfile !== 'boolean') this.hideTeamFromProfile = false
      if (typeof this.supporterTier !== 'number' || !Number.isFinite(this.supporterTier)) {
        this.supporterTier = 0
      }
      this.supporterTier = Math.max(0, Math.min(3, Math.floor(this.supporterTier)))
      if (typeof this.supporterSince !== 'string') this.supporterSince = null
      if (typeof this.hideSupporterBadge !== 'boolean') this.hideSupporterBadge = false
      if (!Array.isArray(this.communityUnlockedCosmeticIds)) {
        this.communityUnlockedCosmeticIds = []
      } else {
        this.communityUnlockedCosmeticIds = this.communityUnlockedCosmeticIds.filter(
          (id): id is string => typeof id === 'string' && id.length > 0,
        )
      }
      if (!this.chosenStarters || typeof this.chosenStarters !== 'object') this.chosenStarters = {}
      if (!Array.isArray(this.battleTowerDefeatedTypes)) this.battleTowerDefeatedTypes = []
      this.battleTowerCapstoneDefeated =
        Math.max(0, Math.floor(Number((this.stats as Record<string, number>)._btCapstoneDefeated ?? 0))) >=
        1
      if (!this.combatAutomation || typeof this.combatAutomation !== 'object') {
        this.combatAutomation = {
          bossVictoryMode: 'manual',
          autoFightBossIfBattable: false,
          autoRetreatToLastFarmRouteOnBossFailure: false,
          masteryAutoFarmMode: 'off',
          masteryAutoFarmTarget: null,
          masteryAutoFarmStopAtZoneTier: 5,
        }
      } else {
        const rawMode = this.combatAutomation.masteryAutoFarmMode
        const masteryMode: MasteryAutoFarmMode =
          rawMode === 'target' || rawMode === 'next_tier' ? rawMode : 'off'
        const rawTarget = this.combatAutomation.masteryAutoFarmTarget
        const masteryTarget =
          rawTarget &&
          typeof rawTarget === 'object' &&
          typeof rawTarget.generation === 'number' &&
          typeof rawTarget.zone === 'number'
            ? {
                generation: Math.floor(rawTarget.generation),
                zone: Math.floor(rawTarget.zone),
              }
            : null
        const stopTier = Math.max(
          1,
          Math.min(
            5,
            Math.floor(Number(this.combatAutomation.masteryAutoFarmStopAtZoneTier ?? 5)),
          ),
        )
        this.combatAutomation = {
          bossVictoryMode:
            this.combatAutomation.bossVictoryMode === 'stay' || this.combatAutomation.bossVictoryMode === 'continue'
              ? this.combatAutomation.bossVictoryMode
              : 'manual',
          autoFightBossIfBattable: Boolean(this.combatAutomation.autoFightBossIfBattable),
          autoRetreatToLastFarmRouteOnBossFailure: Boolean(this.combatAutomation.autoRetreatToLastFarmRouteOnBossFailure),
          masteryAutoFarmMode: masteryMode,
          masteryAutoFarmTarget: masteryTarget,
          masteryAutoFarmStopAtZoneTier: stopTier,
        }
      }
      if (
        !this.lastSelectedFarmRoute ||
        typeof this.lastSelectedFarmRoute.generation !== 'number' ||
        typeof this.lastSelectedFarmRoute.zone !== 'number'
      ) {
        this.lastSelectedFarmRoute = null
      }
      if (
        !this.favoriteFarmRoute ||
        typeof this.favoriteFarmRoute.generation !== 'number' ||
        typeof this.favoriteFarmRoute.zone !== 'number'
      ) {
        this.favoriteFarmRoute = null
      }
      this.audioPreferences = normalizeAudioPreferences(
        incomingAudioPreferences ?? previousAudioPreferences
      )
      if (incomingInclusiveWriting !== undefined) {
        this.inclusiveWriting = Boolean(incomingInclusiveWriting)
      }
      this.offlineShinyReveal = normalizeOfflineShinyRevealPreference(
        (data as Partial<PlayerState>).offlineShinyReveal ?? this.offlineShinyReveal,
      )
      if (data.role !== undefined) this.role = data.role
      if (typeof data.tokens === 'number') this.tokens = data.tokens
      if (typeof data.extraDaycareSlots === 'number') this.extraDaycareSlots = data.extraDaycareSlots
      if (typeof data.offlineCapHours === 'number') {
        const fromServer = clampOfflineCapHours(data.offlineCapHours)
        const local = clampOfflineCapHours(this.offlineCapHours)
        this.offlineCapHours = Math.max(local, fromServer)
      } else if (typeof this.offlineCapHours !== 'number' || isNaN(this.offlineCapHours)) {
        this.offlineCapHours = OFFLINE_CAP_BASE_HOURS
      }
      if (typeof (data as any).offlineGoldEfficiencyPercent === 'number') {
        this.offlineGoldEfficiencyPercent = Math.max(
          OFFLINE_EFFICIENCY_BASE_ABSENT_PERCENT,
          Math.min(
            OFFLINE_EFFICIENCY_BASE_ABSENT_PERCENT + OFFLINE_EFFICIENCY_MAX_BONUS_PERCENT,
            Math.floor(Number((data as any).offlineGoldEfficiencyPercent)),
          ),
        )
      } else if (
        typeof this.offlineGoldEfficiencyPercent !== 'number' ||
        isNaN(this.offlineGoldEfficiencyPercent)
      ) {
        this.offlineGoldEfficiencyPercent = OFFLINE_EFFICIENCY_BASE_ABSENT_PERCENT
      }
      const offlineShopProgress = (data as { offlineShopProgress?: OfflineShopProgress })
        .offlineShopProgress
      if (offlineShopProgress && typeof offlineShopProgress === 'object') {
        this.stats = mergeOfflineShopProgressIntoStats(
          (this.stats ?? {}) as Record<string, number>,
          offlineShopProgress,
        ) as typeof this.stats
        if (typeof offlineShopProgress.tokenShopFullyUnlocked === 'boolean') {
          this.tokenShopFullyUnlocked = offlineShopProgress.tokenShopFullyUnlocked
        }
      } else if (typeof (data as { tokenShopFullyUnlocked?: boolean }).tokenShopFullyUnlocked === 'boolean') {
        this.tokenShopFullyUnlocked = (data as { tokenShopFullyUnlocked: boolean }).tokenShopFullyUnlocked
      }
      if (Array.isArray(data.dailyQuests)) {
        this.dailyQuests = data.dailyQuests
      } else if (!Array.isArray(this.dailyQuests)) {
        this.dailyQuests = []
      }
      if (Array.isArray((data as any).runContracts)) {
        this.runContracts = (data as any).runContracts
      } else if (!Array.isArray(this.runContracts)) {
        this.runContracts = []
      }
      if (Array.isArray((data as any).weeklyQuests)) {
        this.weeklyQuests = (data as any).weeklyQuests
      }
      if (Array.isArray((data as any).monthlyQuests)) {
        this.monthlyQuests = (data as any).monthlyQuests
      }
      const reg = (data as any).regularityState
      if (reg && typeof reg === 'object') {
        this.regularityState = {
          count: Math.max(0, Math.floor(Number(reg.count ?? 0))),
          milestones: Array.isArray(reg.milestones) ? reg.milestones : [],
          nextMilestone: reg.nextMilestone ?? null,
        }
      }
      const cal = (data as any).monthlyCalendar
      if (cal && typeof cal === 'object') {
        this.monthlyCalendar = {
          month: String(cal.month ?? ''),
          daysLogged: Array.isArray(cal.daysLogged) ? cal.daysLogged : [],
          totalDays: Math.max(28, Math.floor(Number(cal.totalDays ?? 30))),
        }
      }
      const c = (data as any).casino
      const mg = (data as any).minigames
      if (mg && typeof mg === 'object') {
        this.minigames = {
          voltorbStartsToday: Math.max(0, Math.floor(Number(mg.voltorbStartsToday ?? 0))),
          voltorbMaxStartsPerDay: Math.max(1, Math.floor(Number(mg.voltorbMaxStartsPerDay ?? 12))),
          voltorbLifetimeWins: Math.max(0, Math.floor(Number(mg.voltorbLifetimeWins ?? 0))),
          quizAnswered: Math.max(0, Math.floor(Number(mg.quizAnswered ?? 0))),
          quizQuestionsPerDay: Math.max(1, Math.floor(Number(mg.quizQuestionsPerDay ?? 5))),
          quizCompleted: Boolean(mg.quizCompleted),
          nextResetAtMs: Math.max(0, Math.floor(Number(mg.nextResetAtMs ?? 0))),
        }
      }
      if (c && typeof c === 'object') {
        this.casino = {
          ...defaultCasinoState(),
          spinsToday: Math.max(0, Math.floor(Number(c.spinsToday ?? 0))),
          goldWageredToday: Math.max(0, Math.floor(Number(c.goldWageredToday ?? 0))),
          maxSpinsPerDay: Math.max(1, Math.floor(Number(c.maxSpinsPerDay ?? 35))),
          maxGoldWageredPerDay: Math.max(0, Math.floor(Number(c.maxGoldWageredPerDay ?? 125_000_000))),
          nextResetAtMs: Math.max(0, Math.floor(Number(c.nextResetAtMs ?? 0))),
          betTiers: Array.isArray(c.betTiers) ? c.betTiers.map((x: unknown) => Math.max(0, Math.floor(Number(x)))) : defaultCasinoState().betTiers,
          tierMinLevel: Array.isArray(c.tierMinLevel)
            ? c.tierMinLevel.map((x: unknown) => Math.max(1, Math.floor(Number(x))))
            : defaultCasinoState().tierMinLevel,
          maxUnlockedTierIndex: Math.max(
            0,
            Math.min(4, Math.floor(Number(c.maxUnlockedTierIndex ?? 0)))
          ),
          paytable: normalizeCasinoPaytable((c as { paytable?: unknown }).paytable),
        }
      }
      if (typeof data.battleTowerLategameDisabled === 'boolean') {
        this.battleTowerLategameDisabled = data.battleTowerLategameDisabled
      }
      if ((data as any).invocationPity && typeof (data as any).invocationPity === 'object') {
        this.invocationPity = (data as any).invocationPity
      } else if (!this.invocationPity || typeof this.invocationPity !== 'object') {
        this.invocationPity = {}
      }
      if (
        (data as any).invocationFirstShinyPity &&
        typeof (data as any).invocationFirstShinyPity === 'object'
      ) {
        const pity = (data as any).invocationFirstShinyPity
        this.invocationFirstShinyPity = {
          eligible: pity.eligible === true,
          withoutShiny: Math.max(0, Math.floor(Number(pity.withoutShiny) || 0)),
          remaining: Math.max(0, Math.floor(Number(pity.remaining) || 0)),
          hardMax: Math.max(1, Math.floor(Number(pity.hardMax) || 1000)),
        }
      }
      // Ancien mode « farm auto » sur Paldea z13 : retour frontière libre
      if (
        this.isEndgame &&
        this.combatGeneration === this.currentGeneration &&
        this.combatZone === this.currentZone
      ) {
        this.combatGeneration = null
        this.combatZone = null
      }
      // Migrate old slug-based defeatedBosses to gen:zone keys (e.g. "g1z12")
      if (this.defeatedBosses.length > 0 && !this.defeatedBosses[0]?.startsWith('g')) {
        const newBosses: string[] = []
        // All zones in previous gens are definitely beaten
        for (let g = 1; g < this.currentGeneration; g++) {
          for (let z = 1; z <= 13; z++) newBosses.push(`g${g}z${z}`)
        }
        // Zones before currentZone in current gen are beaten
        for (let z = 1; z < this.currentZone; z++) {
          newBosses.push(`g${this.currentGeneration}z${z}`)
        }
        // If player is at last gen last zone, check if they beat that boss too
        // (old defeatedBosses would contain the boss slug)
        const lastGen = GENERATIONS.find((g) => g.id === this.currentGeneration)
        if (lastGen && this.currentZone === lastGen.zones.length) {
          const lastBossSlug = lastGen.zones[this.currentZone - 1]?.boss.slug
          if (lastBossSlug && this.defeatedBosses.includes(lastBossSlug)) {
            newBosses.push(`g${this.currentGeneration}z${this.currentZone}`)
          }
        }
        this.defeatedBosses = newBosses
        this.badges = newBosses.length
      }
      // Aligner g9z13 si seul geeta (ou inverse) — même déblocage tour / routes farm
      if (Array.isArray(this.defeatedBosses) && this.defeatedBosses.length > 0) {
        const hasChampion = PALDEA_CHAMPION_BOSS_KEYS.some((k) => this.defeatedBosses.includes(k))
        if (hasChampion && !this.defeatedBosses.includes('g9z13')) {
          this.defeatedBosses = [...this.defeatedBosses, 'g9z13']
        }
      }
      if ((data as any).descenteSanctuaireShards !== undefined) {
        const n = Math.floor(Number((data as any).descenteSanctuaireShards))
        this.descenteSanctuaireShards = Number.isFinite(n) ? Math.max(0, n) : 0
      }
      if ((data as any).descenteSanctuaireRun !== undefined) {
        this.descenteSanctuaireRun = parseDescenteRun((data as any).descenteSanctuaireRun)
      }
      const progressionClamp = this.clampFrontierZoneToPendingBoss()
      const towerClamp = this.clampInvalidBattleTowerZone()
      const repairedBossKeys = this.repairMissingBossKeysFromFrontierProgress()
      this.regionUnlockMessage = null
      this.zoneVictoryPrompt = null
      this.zoneAdvanceReady = getCompletedFrontierAdvanceTarget(this)
      this.reconcileFarmRouteWithProgress()
      this.saveBonuses()
      return repairedBossKeys + (progressionClamp ? 1 : 0) + (towerClamp ? 1 : 0)
    },

    /** Réserve (z20) sans 18 types → recale sur la prochaine épreuve jouable. */
    clampInvalidBattleTowerZone(): boolean {
      if (this.battleTowerZone !== BATTLE_TOWER_FARM_ZONE_INDEX) return false
      if (battleTowerFarmUnlocked(this.battleTowerDefeatedTypes)) return false
      this.enterBattleTower(
        getNextBattleTowerZoneIndex(this.battleTowerDefeatedTypes, this.battleTowerCapstoneDefeated),
      )
      return true
    },

    /**
     * Si la zone frontière dépasse la première ligue non validée (désynchro client / bouton « Continuer »
     * avec état résiduel), on recale pour éviter de bloquer la progression (ex. Ondine sans Pierre).
     */
    clampFrontierZoneToPendingBoss(): boolean {
      if (this.battleTowerZone != null) return false
      if (this.combatGeneration != null) return false
      if (!Array.isArray(this.defeatedBosses)) this.defeatedBosses = []
      const cap = getFirstFrontierZoneWithPendingBoss(this.currentGeneration, this.defeatedBosses)
      if (this.currentZone <= cap) return false
      this.currentZone = cap
      this.currentStage = 1
      this.stageKills = 0
      return true
    },

    /**
     * Si la progression (currentGeneration / currentZone) indique des zones déjà franchies mais que
     * les clés `g{gen}z{zone}` manquent (désynchro sauvegarde), on les réintègre et on remonte `badges`.
     * Ne s’applique pas à la tour de combat.
     */
    repairMissingBossKeysFromFrontierProgress(): number {
      if (this.battleTowerZone != null) return 0
      if (!Array.isArray(this.defeatedBosses)) this.defeatedBosses = []
      const set = new Set<string>(this.defeatedBosses.filter((boss): boss is string => typeof boss === 'string' && boss.length > 0))
      let added = 0
      let highestChosenStarterGen = 0
      for (const [key, slug] of Object.entries(this.chosenStarters ?? {})) {
        if (typeof slug !== 'string' || slug.length === 0) continue
        const g = Math.floor(Number(key))
        if (g >= 2) highestChosenStarterGen = Math.max(highestChosenStarterGen, g)
      }
      const progressGenCap = Math.max(this.currentGeneration, highestChosenStarterGen)
      for (const g of GENERATIONS) {
        if (g.id > progressGenCap) break
        for (const z of g.zones) {
          if (g.id === progressGenCap && z.id >= this.currentZone) break
          const key = `g${g.id}z${z.id}`
          if (!set.has(key)) {
            set.add(key)
            added++
          }
        }
      }
      if (added === 0) return 0
      this.defeatedBosses = [...set]
      const keyCount = [...set].filter((k) => /^g\d+z\d+$/.test(k)).length
      this.badges = Math.max(this.badges ?? 0, keyCount)
      return added
    },

    markStarterChosen(gen: number, slug: string) {
      if (!this.chosenStarters) this.chosenStarters = {}
      this.chosenStarters[gen] = slug
    },

    enterBattleTower(zoneIndex: number) {
      this.battleTowerZone = zoneIndex
    },

    enterBattleTowerAtProgress() {
      this.zoneVictoryPrompt = null
      this.zoneAdvanceReady = null
      this.enterBattleTower(
        getNextBattleTowerZoneIndex(this.battleTowerDefeatedTypes, this.battleTowerCapstoneDefeated),
      )
    },

    markBattleTowerCapstoneDefeated() {
      if (this.battleTowerCapstoneDefeated) return
      this.battleTowerCapstoneDefeated = true
      if (!this.stats || typeof this.stats !== 'object') this.stats = {}
      ;(this.stats as Record<string, number>)._btCapstoneDefeated = 1
    },

    leaveBattleTower() {
      this.battleTowerZone = null
    },

    recordBattleTowerBossWin(type: PokemonType) {
      if (this.battleTowerDefeatedTypes.includes(type)) return
      this.battleTowerDefeatedTypes = [...this.battleTowerDefeatedTypes, type]
    },

    reset() {
      this.$reset()
    },
  },
})
